import React, { FC, useEffect, useRef, useState } from 'react'
import { Button, LoaderSpinner, Modal } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import {
  createTerminalSaveSession,
  getTerminalSaveSession,
  TERMINAL_SAVE_SESSION_STATUS,
} from '../../../../functions/payment-types/lspayTerminalCardSave'

const INITIAL_POLL_DELAY_MS = 5000
const POLLING_INTERVAL_MS = 1000
const MAX_POLL_ERRORS = 3
const MAX_POLL_DURATION_MS = 60000
const CARD_DOTS = '\u2022\u2022\u2022\u2022'

interface TerminalCardSaveModalProps {
  merchantReference: string
  customerReference: string
  customerName: string
  pspTerminalId: string
  pli: string
  cardBrand: string
  cardLastFour: string
  saleId?: string
  createdBy?: string
  metadata?: Record<string, string>
  onSuccess: () => void
  onError: (message: string) => void
  onCancelled: () => void
}

export const TerminalCardSaveModal: FC<TerminalCardSaveModalProps> = ({
  merchantReference,
  customerReference,
  customerName,
  pspTerminalId,
  pli,
  cardBrand,
  cardLastFour,
  saleId,
  createdBy,
  metadata,
  onSuccess,
  onError,
  onCancelled,
}) => {
  const t = useT()
  const active = useRef(true)
  const [isCreating, setIsCreating] = useState(true)

  const title = t(
    "Save {cardBrand} {dots} {cardLastFour} to {customerName}'s profile",
    {
      cardBrand,
      dots: CARD_DOTS,
      cardLastFour,
      customerName,
      _context: 'Modal title when saving a card on file via terminal',
    }
  )

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>

    const startSession = async () => {
      try {
        const { saveSessionId } = await createTerminalSaveSession({
          merchantReference,
          customerReference,
          pspTerminalId,
          pli,
          saleId,
          createdBy,
          metadata,
        })

        if (!active.current) return
        setIsCreating(false)

        let errorCount = 0
        const pollStartTime = Date.now()

        const pollStatus = async () => {
          if (!active.current) return

          if (Date.now() - pollStartTime > MAX_POLL_DURATION_MS) {
            onError(
              t('Card save timed out. Please try again.', {
                _context:
                  'Error message when terminal card save polling exceeds max duration',
              })
            )
            return
          }

          try {
            const result = await getTerminalSaveSession({
              merchantReference,
              customerReference,
              saveSessionId,
              pli,
            })

            if (!active.current) return

            errorCount = 0
            const { status, failureReason } = result

            if (status === TERMINAL_SAVE_SESSION_STATUS.SUCCEEDED) {
              onSuccess()
              return
            }

            if (status === TERMINAL_SAVE_SESSION_STATUS.FAILED) {
              onError(
                failureReason ||
                  t('Card save failed', {
                    _context: 'Error message when terminal card save fails',
                  })
              )
              return
            }

            if (status === TERMINAL_SAVE_SESSION_STATUS.CANCELLED) {
              onCancelled()
              return
            }

            // in_progress or requires_action — keep polling
            timeoutId = setTimeout(pollStatus, POLLING_INTERVAL_MS)
          } catch {
            errorCount++
            if (!active.current) return

            if (errorCount >= MAX_POLL_ERRORS) {
              onError(
                t('Unable to check card save status. Please try again.', {
                  _context:
                    'Error message when polling for terminal card save status fails repeatedly',
                })
              )
              return
            }

            timeoutId = setTimeout(pollStatus, POLLING_INTERVAL_MS)
          }
        }

        // Start polling after initial delay
        timeoutId = setTimeout(pollStatus, INITIAL_POLL_DELAY_MS)
      } catch {
        if (active.current) {
          onError(
            t('Failed to initiate card save on terminal', {
              _context:
                'Error message when creating terminal card save session fails',
            })
          )
        }
      }
    }

    startSession()

    return () => {
      active.current = false
      clearTimeout(timeoutId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Modal dismissable={false} focusTrapActive={true} size="small">
      <div className="vd-pa6">
        <div style={{ textAlign: 'left', fontWeight: 'bold' }}>{title}</div>
        <div className="vd-flex vd-flex--align-center vd-mt4">
          <div style={{ flexShrink: 0 }}>
            <LoaderSpinner small />
          </div>
          <span className="vd-text--secondary vd-ml2">
            {isCreating
              ? t('Request sent to terminal', {
                  _context:
                    'Status message while initiating card save on terminal',
                })
              : t('Waiting for customer to complete process on terminal', {
                  _context:
                    'Status message while waiting for customer to save card on terminal',
                })}
          </span>
        </div>
        <div className="vd-flex vd-flex--justify-end vd-mt5">
          <Button variant="no" onClick={onCancelled}>
            {t('Cancel', {
              _context: 'Cancel button on terminal card save modal',
            })}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
